/**
 * Certificates of Occupancy Repository
 *
 * The scraped Certificate of Occupancy listings for one property, read by the
 * Quick Links popup. Two sources, two custom methods: BIS holds certificates
 * issued before roughly March 2021, DOB NOW everything after, so most buildings
 * have rows in one and not the other.
 *
 * Read-only. There is no CRUD here -- nothing in the app creates or edits a
 * certificate; the scrapers fill both tables server-side.
 *
 * Custom methods rather than the generated CRUD because each source is its own
 * endpoint under the property, not two collections under one. createRepository
 * derives the state from each method name, so getBisCertificates gives
 * `bisCertificates`, `bisCertificatesPagination`, `isLoadingBisCertificates` and
 * `bisCertificatesError`, and the DOB NOW equivalents -- and unwraps the
 * Laravel paginator shape these endpoints return without any help.
 */

import { createRepository } from 'data/utils/createRepository';
import { getApiUrl } from 'data/services/apiConstants';
import { get } from 'data/services/apiService';

/** Rows per page in the popup's table. */
export const CERTIFICATES_PER_PAGE = 25;

/** The BIS half of the popup. */
export const SOURCE_BIS = 'bis';

/** The DOB NOW half of the popup. */
export const SOURCE_DOBNOW = 'dobnow';

/**
 * Builds the query string the listing endpoints accept.
 *
 * @param {number} page
 * @param {string} filter Search term; omitted when empty.
 * @returns {string}
 */
const listingQuery = (page = 1, filter = '') => {
	const params = new URLSearchParams({
		page: String(page || 1),
		per_page: String(CERTIFICATES_PER_PAGE),
	});

	if (filter) {
		params.set('filter', filter);
	}

	return params.toString();
};

const certificatesOfOccupancyRepository = createRepository({
	endpoint: 'property',
	name: 'certificatesOfOccupancy',
	statePath: 'certificatesOfOccupancy',

	customMethods: {
		/**
		 * The BIS listing for a property.
		 * Usage: actions.getBisCertificates({ propertyId, page, filter })
		 */
		getBisCertificates: ({ propertyId, page, filter, authToken, dispatch }) =>
			get({
				url: `${getApiUrl()}/api/property/${propertyId}/bis/certificates-of-occupancy?${listingQuery(page, filter)}`,
				authToken,
				dispatch,
			}),

		/**
		 * The DOB NOW listing for a property.
		 * Usage: actions.getDobNowCertificates({ propertyId, page, filter })
		 */
		getDobNowCertificates: ({ propertyId, page, filter, authToken, dispatch }) =>
			get({
				url: `${getApiUrl()}/api/property/${propertyId}/dobnow/certificates-of-occupancy?${listingQuery(page, filter)}`,
				authToken,
				dispatch,
			}),
	},
});

export const {
	statePath,
	thunks: certificatesOfOccupancyThunks,
	reducer: certificatesOfOccupancyReducer,
	actions: certificatesOfOccupancyActions,
	types: certificatesOfOccupancyTypes,
	selectors: certificatesOfOccupancySelectors,
	useHook: useCertificatesOfOccupancy,
} = certificatesOfOccupancyRepository;

export default certificatesOfOccupancyRepository;
